// screens/marketplace/shared.jsx
import React from "react";
import { ArrowLeft, Package, Inbox, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";

// ── Theme helpers ─────────────────────────────────────────
export const useTokens = () => {
  const { colors } = useTheme();
  return {
    bg: colors.mainBg,
    surface: colors.cardBg,
    surfaceRaised: colors.raisedBg,
    subtle: colors.subtleBg || colors.hoverBg,
    text: colors.text,
    heading: colors.heading,
    muted: colors.secondaryText,
    border: colors.border,
    separator: colors.separator || colors.border,
    accent: colors.btnBg,
    accentText: colors.btnText,
    accentSoft: colors.hoverBg,
    success: colors.success,
    successBg: colors.successBg,
    error: colors.error,
    errorBg: colors.errorBg,
    warning: colors.warning,
    warningBg: colors.warningBg,
    shadow: colors.shadow,
    shadowStrong: colors.shadowStrong,
    scrim: colors.scrim || "rgba(0,0,0,0.5)",
  };
};

// ── Page shell ────────────────────────────────────────────
export const PageShell = ({ eyebrow, title, subtitle, actions, children }) => {
  const t = useTokens();
  const navigate = useNavigate();
  return (
    <div
      style={{
        maxWidth: 1180,
        margin: "0 auto",
        padding: "20px 16px 60px",
        color: t.text,
      }}
    >
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: t.muted,
          cursor: "pointer",
          padding: "6px 0",
          marginBottom: 10,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <header
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 14,
          marginBottom: 24,
        }}
      >
        <div style={{ minWidth: 0 }}>
          {eyebrow && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: t.accent,
                marginBottom: 6,
              }}
            >
              {eyebrow}
            </div>
          )}
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(22px, 3vw, 30px)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: t.heading,
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 14,
                color: t.muted,
                lineHeight: 1.55,
                maxWidth: 620,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {actions}
          </div>
        )}
      </header>

      {children}
    </div>
  );
};

// ── Section card ──────────────────────────────────────────
export const Section = ({
  title,
  description,
  icon: Icon,
  chip,
  children,
  footer,
}) => {
  const t = useTokens();
  return (
    <section
      style={{
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
      }}
    >
      {(title || chip) && (
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
            {Icon && (
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: t.subtle,
                  color: t.accent,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={16} />
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  color: t.heading,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </h2>
              {description && (
                <p
                  style={{
                    margin: "3px 0 0",
                    fontSize: 13,
                    color: t.muted,
                    lineHeight: 1.5,
                  }}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
          {chip && (
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                background: t.subtle,
                color: t.muted,
                fontSize: 11.5,
                fontWeight: 700,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {chip}
            </span>
          )}
        </header>
      )}
      {children}
      {footer && (
        <footer
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${t.separator}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          {footer}
        </footer>
      )}
    </section>
  );
};

// ── Buttons ───────────────────────────────────────────────
export const Button = ({
  variant = "primary",
  size = "md",
  icon: Icon,
  children,
  ...rest
}) => {
  const t = useTokens();
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid transparent",
    borderRadius: 10,
    fontWeight: 600,
    cursor: "pointer",
    transition:
      "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease",
    fontFamily: "inherit",
  };
  const sizes = {
    sm: { padding: "6px 10px", fontSize: 12 },
    md: { padding: "9px 14px", fontSize: 13.5 },
    lg: { padding: "11px 18px", fontSize: 14.5 },
  };
  const variants = {
    primary: {
      background: t.accent,
      color: t.accentText,
      boxShadow: `0 2px 8px ${t.accent}33`,
    },
    secondary: {
      background: t.surfaceRaised,
      color: t.text,
      border: `1px solid ${t.border}`,
    },
    ghost: { background: "transparent", color: t.muted },
    danger: { background: t.error, color: "#fff" },
    dangerGhost: {
      background: "transparent",
      color: t.error,
      border: `1px solid ${t.error}55`,
    },
  };
  return (
    <button
      type="button"
      {...rest}
      style={{ ...base, ...sizes[size], ...variants[variant], ...rest.style }}
      onMouseEnter={(e) => {
        if (variant === "primary") e.currentTarget.style.transform = "translateY(-1px)";
        else if (variant === "secondary") e.currentTarget.style.borderColor = t.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        if (variant === "secondary") e.currentTarget.style.borderColor = t.border;
      }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 15} />}
      {children}
    </button>
  );
};

// ── Form fields ───────────────────────────────────────────
export const Field = ({ label, error, hint, children }) => {
  const t = useTokens();
  return (
    <label style={{ display: "block", minWidth: 0 }}>
      {label && (
        <span
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: t.text,
            marginBottom: 6,
          }}
        >
          {label}
        </span>
      )}
      {children}
      {error && (
        <span
          style={{
            display: "block",
            fontSize: 11.5,
            color: t.error,
            marginTop: 4,
          }}
        >
          {error}
        </span>
      )}
      {hint && !error && (
        <span
          style={{
            display: "block",
            fontSize: 11.5,
            color: t.muted,
            marginTop: 4,
          }}
        >
          {hint}
        </span>
      )}
    </label>
  );
};

export const Input = (props) => {
  const t = useTokens();
  return (
    <input
      {...props}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        color: t.text,
        fontSize: 14,
        outline: "none",
        fontFamily: "inherit",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        ...props.style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = t.accent;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${t.accent}22`;
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = t.border;
        e.currentTarget.style.boxShadow = "none";
        props.onBlur?.(e);
      }}
    />
  );
};

export const Textarea = (props) => {
  const t = useTokens();
  return (
    <textarea
      {...props}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        color: t.text,
        fontSize: 14,
        outline: "none",
        minHeight: 92,
        resize: "vertical",
        fontFamily: "inherit",
        ...props.style,
      }}
    />
  );
};

export const Select = ({ children, ...props }) => {
  const t = useTokens();
  return (
    <select
      {...props}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.surfaceRaised,
        color: t.text,
        fontSize: 14,
        outline: "none",
        fontFamily: "inherit",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(
          t.muted
        )}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 10px center",
        paddingRight: 32,
        ...props.style,
      }}
    >
      {children}
    </select>
  );
};

// ── Empty / loading states ────────────────────────────────
export const EmptyState = ({ icon: Icon = Inbox, title, description, action }) => {
  const t = useTokens();
  return (
    <div
      style={{
        textAlign: "center",
        padding: "48px 24px",
        border: `1px dashed ${t.border}`,
        borderRadius: 14,
        color: t.muted,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: t.subtle,
          color: t.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Icon size={22} />
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: t.heading,
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      {description && (
        <p
          style={{
            margin: "0 auto",
            fontSize: 13,
            maxWidth: 340,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      )}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
};

export const LoadingBlock = ({ label = "Loading…" }) => {
  const t = useTokens();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 60,
        gap: 12,
        color: t.muted,
        fontSize: 13,
      }}
    >
      <Loader2
        size={26}
        style={{ animation: "mp-spin 0.9s linear infinite", color: t.accent }}
      />
      {label}
      <style>{`@keyframes mp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Form grid helper ──────────────────────────────────────
export const FormGrid = ({ columns = 4, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: 12,
    }}
    className={`mp-grid-${columns}`}
  >
    {children}
    <style>{`
      @media (max-width: 900px) { .mp-grid-${columns} { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
      @media (max-width: 520px) { .mp-grid-${columns} { grid-template-columns: 1fr !important; } }
    `}</style>
  </div>
);

export const GridSpan = ({ span = "full", children }) => (
  <div style={{ gridColumn: span === "full" ? "1 / -1" : `span ${span}` }}>
    {children}
  </div>
);

// ── Error parser ──────────────────────────────────────────
export const parseApiError = (data) => {
  const errors = data?.errors;
  if (!errors) return data?.message || "Request could not be completed.";
  return Object.entries(errors)
    .map(([key, value]) => {
      const message = Array.isArray(value)
        ? value.join(", ")
        : typeof value === "object"
        ? JSON.stringify(value)
        : value;
      return `${key.replaceAll("_", " ")}: ${message}`;
    })
    .join(" ");
};

// ── Image optimizer (shared by product form) ──────────────
export const optimizeImage = async (file) => {
  if (!file || !file.type.startsWith("image/") || file.size < 350 * 1024)
    return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d", { alpha: false }).drawImage(
      bitmap,
      0,
      0,
      canvas.width,
      canvas.height
    );
    bitmap.close();
    const blob = await new Promise((r) =>
      canvas.toBlob(r, "image/webp", 0.82)
    );
    return blob && blob.size < file.size
      ? new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.webp`, {
          type: "image/webp",
        })
      : file;
  } catch {
    return file;
  }
};